import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "cn";
import type * as React from "react";

/**
 * Два варианта — `warning` и `error`, каждый со своей ролью ARIA: `warning`
 * объявляет себя `status` (не срочно, можно дождаться следующей паузы читалки
 * экрана), `error` — `alert` (прерывает немедленно). Роль — часть контракта
 * варианта, а не отдельный проп: перепутать их с места использования нельзя.
 *
 * Цвет текста — тоже часть варианта, не проп потребителя: заголовок несёт
 * цвет по наследованию (`color` каскадом от корня, у `AlertTitle` своего
 * цвета нет), описание — своим селектором `*:data-[slot=alert-description]`
 * поверх базового `text-muted-foreground` (приём shadcn: `data-slot`
 * помечает роль элемента, вариант красит её с уровня корня). Потребителю
 * незачем красить `AlertTitle`/`AlertDescription` вручную — цвет уже пришёл
 * с вариантом (ревью stage6-acceptance-debts, второй круг, пункт 4).
 */
const alertVariants = cva(
  "flex w-full items-start gap-2 border px-3 [&>svg]:mt-0.5 [&>svg]:size-4 [&>svg]:shrink-0",
  {
    variants: {
      size: {
        default: "rounded-lg py-2 text-sm",
        sm: "rounded-sm py-2.5 text-xs leading-[1.45]",
      },
      variant: {
        warning:
          "border-warning/30 bg-warning-wash text-warning [&>svg]:text-warning *:data-[slot=alert-description]:text-warning/90",
        error:
          "border-error/30 bg-error-wash text-error [&>svg]:text-error *:data-[slot=alert-description]:text-error/90",
      },
    },
    defaultVariants: {
      variant: "warning",
      size: "default",
    },
  },
);

function Alert({
  className,
  variant = "warning",
  size = "default",
  ...props
}: React.ComponentProps<"div"> & VariantProps<typeof alertVariants>) {
  return (
    <div
      data-slot="alert"
      data-variant={variant}
      data-size={size}
      role={variant === "error" ? "alert" : "status"}
      className={cn(alertVariants({ variant, size }), className)}
      {...props}
    />
  );
}

function AlertTitle({ className, ...props }: React.ComponentProps<"div">) {
  return <div data-slot="alert-title" className={cn("font-medium", className)} {...props} />;
}

function AlertDescription({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-description"
      className={cn("text-muted-foreground", className)}
      {...props}
    />
  );
}

export { Alert, AlertDescription, AlertTitle, alertVariants };
